import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Label } from "./ui/label";
import { CheckCircle, AlertTriangle, Trash2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API client setup
const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ManageBudgets = ({ selectedMonth, selectedYear, monthNames }) => {
  const [projects, setProjects] = useState([]);
  const [monthlyBudgets, setMonthlyBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [budgetInputs, setBudgetInputs] = useState({});
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, budgetsRes] = await Promise.all([
        apiClient.get("/projects"),
        apiClient.get(`/monthly-budgets/${selectedYear}/${selectedMonth}`)
      ]);
      
      setProjects(projectsRes.data);
      setMonthlyBudgets(budgetsRes.data);
      
      // Initialize budget inputs from existing data
      const inputs = {};
      budgetsRes.data.forEach(budget => {
        inputs[budget.project_id] = budget.budget_hours.toString();
      });
      setBudgetInputs(inputs);
    } catch (error) {
      console.error("Error fetching budget data:", error);
      showAlert("Error loading budget data. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type = "info") => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleBudgetInputChange = (projectId, value) => {
    setBudgetInputs(prev => ({
      ...prev,
      [projectId]: value
    }));
  };

  const setBudget = async (projectId) => {
    const budgetHours = parseFloat(budgetInputs[projectId]);
    
    if (isNaN(budgetHours) || budgetHours < 0) {
      showAlert("Please enter a valid budget amount (must be 0 or greater).", "error");
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('/monthly-budgets', {
        project_id: projectId,
        year: selectedYear,
        month: selectedMonth,
        budget_hours: budgetHours
      });
      
      await fetchData();
      showAlert("Budget set successfully!", "success");
    } catch (error) {
      console.error("Error setting budget:", error);
      showAlert("Error setting budget. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteBudget = async (budgetId) => {
    if (!window.confirm("Are you sure you want to remove this budget? This will set the project to unlimited budget.")) {
      return;
    }

    setSaving(true);
    try {
      await apiClient.delete(`/monthly-budgets/${budgetId}`);
      await fetchData();
      showAlert("Budget removed successfully!", "success");
    } catch (error) {
      console.error("Error deleting budget:", error);
      showAlert("Error removing budget. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const getCurrentBudget = (projectId) => {
    const budget = monthlyBudgets.find(b => b.project_id === projectId);
    return budget ? budget.budget_hours : null;
  };

  const getBudgetId = (projectId) => {
    const budget = monthlyBudgets.find(b => b.project_id === projectId);
    return budget ? budget.id : null;
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading budget data...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Monthly Budget Management - {monthNames[selectedMonth - 1]} {selectedYear}</CardTitle>
          <CardDescription>
            Set specific budget amounts for each project for {monthNames[selectedMonth - 1]} {selectedYear}. 
            Budgets are project-specific and month-specific. Each project can have different budgets for different months.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {alert && (
            <Alert className={`mb-4 ${alert.type === 'error' ? 'border-red-200 bg-red-50' : alert.type === 'success' ? 'border-green-200 bg-green-50' : ''}`}>
              {alert.type === 'success' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : alert.type === 'error' ? (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertDescription className={alert.type === 'error' ? 'text-red-700' : alert.type === 'success' ? 'text-green-700' : ''}>
                {alert.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 p-3 text-left font-semibold">Client</th>
                  <th className="border border-gray-300 p-3 text-left font-semibold">Project</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">Current Budget ({monthNames[selectedMonth - 1]} {selectedYear})</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">Set New Budget (hours)</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const currentBudget = getCurrentBudget(project.id);
                  const budgetId = getBudgetId(project.id);
                  
                  return (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 p-3 font-medium">{project.client_name}</td>
                      <td className="border border-gray-300 p-3">{project.name}</td>
                      <td className="border border-gray-300 p-3 text-center">
                        {currentBudget !== null ? (
                          <Badge variant="outline" className="font-mono">
                            {currentBudget}h
                          </Badge>
                        ) : (
                          <Badge variant="secondary">No limit</Badge>
                        )}
                      </td>
                      <td className="border border-gray-300 p-3 text-center">
                        <div className="flex justify-center">
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            placeholder="40"
                            className="w-24 text-center"
                            value={budgetInputs[project.id] || ''}
                            onChange={(e) => handleBudgetInputChange(project.id, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                setBudget(project.id);
                              }
                            }}
                            data-testid={`budget-input-${project.id}`}
                          />
                        </div>
                      </td>
                      <td className="border border-gray-300 p-3 text-center">
                        <div className="flex gap-2 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBudget(project.id)}
                            disabled={saving || !budgetInputs[project.id]}
                            data-testid={`set-budget-${project.id}`}
                          >
                            {saving ? "Setting..." : "Set Budget"}
                          </Button>
                          {currentBudget !== null && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteBudget(budgetId)}
                              disabled={saving}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`remove-budget-${project.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {projects.length === 0 && (
                  <tr>
                    <td colSpan={5} className="border border-gray-300 p-6 text-center text-gray-500">
                      No projects found. Create some projects to manage budgets.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-md">
            <div className="text-sm text-blue-800">
              <strong>Instructions:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Enter budget hours and press Enter or click "Set Budget" to apply for {monthNames[selectedMonth - 1]} {selectedYear}</li>
                <li>Leave empty or click the trash icon to remove budget limits (unlimited budget)</li>
                <li>Budget changes take effect immediately and apply only to the selected month</li>
                <li>Projects without budget limits will show as "No limit" in reports</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageBudgets;